import { Link } from "react-router-dom";
import { COMPANY } from "../lib/company";

const STEPS = [
  {
    n: "01",
    tone: "blue",
    title: "Connect the accounts you publish to",
    body: "Authorise on Facebook or Google, choose the Pages, Instagram Business accounts, and YouTube channels you want, and return to your workspace.",
    note: "Your social password stays with Facebook or Google. Simino Social receives a revocable access token instead.",
    visual: <div className="hiw-connections" aria-hidden="true"><span className="network-dot fb">f</span><i></i><span className="network-dot ig">◎</span><i></i><span className="network-dot yt">▶</span><b>Connected</b></div>,
  },
  {
    n: "02",
    tone: "violet",
    title: "Compose once and choose destinations",
    body: "Write the caption, attach a photo or video, and select every eligible account that should receive it. YouTube appears for video posts, while Facebook and Instagram support their compatible media types.",
    note: "Reuse media from your private library without uploading the same file again.",
    visual: <div className="hiw-composer" aria-hidden="true"><span>Write your caption…</span><div><b>＋ Media</b><i>f</i><i>◎</i><i>▶</i></div></div>,
  },
  {
    n: "03",
    tone: "cyan",
    title: "Check the file before it is queued",
    body: "Simino Social reads the file type, size, dimensions, aspect ratio, and duration, then checks those facts against the rules for every selected destination.",
    note: "A problem is shown beside the affected destination before you publish—not after the scheduled time has passed.",
    visual: <div className="hiw-checks" aria-hidden="true"><span><i>✓</i> Facebook ready</span><span><i>✓</i> Instagram ready</span><span><i>✓</i> YouTube ready</span></div>,
  },
  {
    n: "04",
    tone: "green",
    title: "Publish now or choose a time",
    body: "Send an approved post immediately or select a future date and time. Scheduling runs on the server, so closing the browser or turning off your device does not stop the clock.",
    note: "Role permissions keep creating, approving, scheduling, and publishing as separate authorities.",
    visual: <div className="hiw-schedule" aria-hidden="true"><span>16</span><div><small>FRIDAY</small><b>09:30 AM</b></div><em>Scheduled</em></div>,
  },
  {
    n: "05",
    tone: "amber",
    title: "See what happened at every destination",
    body: "A multi-network post is not reduced to one vague status. Each destination keeps its own progress, provider response, error, retry state, and link to the live post when available.",
    note: "One failed destination does not hide the destinations that published successfully.",
    visual: <div className="hiw-results" aria-hidden="true"><span><i className="fb">f</i><b>Published</b><small>View post ↗</small></span><span><i className="ig">◎</i><b>Published</b><small>View post ↗</small></span><span><i className="yt">▶</i><b>Processing</b><small>Checking again</small></span></div>,
  },
];

export function HowItWorks() {
  return (
    <>
      <section className="hiw-hero">
        <div className="hiw-hero-inner">
          <p className="hero-badge"><span aria-hidden="true">✦</span> From connection to confirmation</p>
          <h1>One clear path from<br /><span>draft to delivered.</span></h1>
          <p className="lede">
            {COMPANY.product} brings account selection, media checks, scheduling, publishing,
            and per-network results into one understandable workflow.
          </p>
          <div className="hero-cta">
            <Link to="/signin" className="btn-link primary lg">Start publishing <span aria-hidden="true">→</span></Link>
            <a href="#workflow" className="btn-link lg">Explore the workflow <span aria-hidden="true">↓</span></a>
          </div>
          <div className="hiw-path" aria-label="Workflow summary">
            <span>Connect</span><i>→</i><span>Compose</span><i>→</i><span>Check</span><i>→</i><span>Schedule</span><i>→</i><span>Track</span>
          </div>
        </div>
      </section>

      <section className="hiw-workflow" id="workflow">
        <div className="band-inner">
          <div className="hiw-section-head">
            <p className="eyebrow">How publishing works</p>
            <h2>Five steps. No mystery in the middle.</h2>
            <p>Each step answers a practical question: where is this going, will the media work, when should it go, and what actually happened?</p>
          </div>
          <div className="hiw-steps">
            {STEPS.map((step, index) => (
              <article className={`hiw-step ${step.tone}`} key={step.n}>
                <div className="hiw-step-copy">
                  <span className="hiw-number">{step.n}</span>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                  <p className="hiw-note"><span aria-hidden="true">✓</span>{step.note}</p>
                </div>
                <div className="hiw-step-visual">
                  <span className="hiw-visual-label">{index === 4 ? "Live delivery view" : "Inside the workflow"}</span>
                  {step.visual}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="hiw-trust">
        <div className="band-inner">
          <div className="hiw-trust-copy">
            <p className="eyebrow">Designed for real platform rules</p>
            <h2>Simple on the surface. Explicit underneath.</h2>
            <p>
              Facebook, Instagram, and YouTube do not publish media in the same way. Simino Social
              handles each destination independently, while keeping the experience consistent for you.
            </p>
            <Link to="/security">See how accounts and media are protected →</Link>
          </div>
          <div className="hiw-facts">
            <div><strong>Private media</strong><span>Uploads stay in workspace-scoped storage.</span></div>
            <div><strong>Server-side schedule</strong><span>The publishing clock does not depend on an open tab.</span></div>
            <div><strong>Per-network truth</strong><span>Every destination reports its own outcome.</span></div>
            <div><strong>Revocable access</strong><span>Disconnect in the app or at the platform.</span></div>
          </div>
        </div>
      </section>

      <section className="hiw-cta">
        <div>
          <p className="eyebrow">Ready when you are</p>
          <h2>Turn the next post into a clear, trackable workflow.</h2>
          <p>No card required to start with the web app.</p>
          <Link to="/signin" className="btn-link primary lg">Get started free <span aria-hidden="true">→</span></Link>
        </div>
      </section>
    </>
  );
}
